import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import EavAttributeField from './EavAttributeField';

/**
 * EAV attribute group card.
 *
 * Props:
 *   groupLabel      - Display name for the group heading
 *   attributes      - Array of attribute definitions
 *   values          - { attribute_code: value, ... } lookup object
 *   onAttributeChange(code, val)  - Called when a field value changes
 *   viewOnly        - If true, fields render in read-only mode
 *   isCollapsed     - (optional) External collapse state
 *   onToggle        - (optional) Callback when header is clicked (for external state)
 *   showCount       - (optional) If true, shows attribute count badge in header
 *   className       - (optional) Extra classes for the outer container
 *   channelStoreId  - (optional) Store ID for M2 category_ids dropdown
 */
const EavGroup = ({
  groupLabel,
  attributes,
  values,
  onAttributeChange,
  viewOnly = false,
  isCollapsed: externalCollapsed,
  onToggle,
  showCount = false,
  className = '',
  channelStoreId,
}) => {
  // Internal collapse state when external props not provided
  const [internalOpen, setInternalOpen] = React.useState(true);
  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : !internalOpen;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalOpen(prev => !prev);
    }
  };

  return (
    <div className={`border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm mb-6 bg-white dark:bg-slate-800 ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        className="w-full p-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
      >
        <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
          <div className="w-1 h-4 bg-blue-500 rounded-full flex-shrink-0"></div>
          {groupLabel}
        </span>
        <span className="flex items-center gap-2">
          {showCount && (
            <span className="text-xs font-medium text-slate-400 dark:text-slate-500 bg-white dark:bg-slate-700/80 px-2.5 py-0.5 rounded-full border border-slate-200 dark:border-slate-600">
              {attributes.length}
            </span>
          )}
          {isCollapsed ? <ChevronDown size={18} className="text-slate-400 dark:text-slate-500" /> : <ChevronUp size={18} className="text-slate-400 dark:text-slate-500" />}
        </span>
      </button>

      {!isCollapsed && (
        <div className="p-6 grid grid-cols-1 gap-x-8 gap-y-6">
          {attributes.map(attr => (
            <EavAttributeField
              key={attr.attribute_id}
              attr={attr}
              value={values[attr.attribute_code] || ''}
              onChange={onAttributeChange}
              viewOnly={viewOnly}
              channelStoreId={channelStoreId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EavGroup;
